import { BillingService, isActiveUserBilling, OrgBillingPlan, OrgBillingSubscription } from 'models/billing_service'
import { ALLOWED_PLANS, REFFERAL_ALLOWED_PLANS, shouldShowContactSalesButton } from './plans'

/**
 * Every billing gate that behaves differently on an active-user plan (GNB-3307, dashboard plan Phase 1), as pure
 * functions. For old plans each returns exactly what the inline expression it replaced returned; only
 * `isActiveUserBilling(org)` changes the answer. Missing plan data fails closed: an active-user plan whose meta could
 * not be read is treated as sales only.
 */

type OrgLike = {product?: string, billingService?: Pick<BillingService, 'pricingType'|'planId'>|null}
type PlanLike = Pick<OrgBillingPlan, 'onlyThroughSales'|'cancelOnlyThroughSales'|'level'>|null|undefined

const planIdOf = (org: OrgLike) => org.billingService?.planId

/** Enterprise on active-user plans; on Pro and Pro+ the billing manager buys users and AI Tasks here (D14). */
export function isActiveUserSalesOnly(plan: PlanLike) {
  return plan?.onlyThroughSales !== false
}

/** The AI Tasks row and the staff "Add One-Time AI Tasks" button (was `is0101Plan`). */
export function hasAiAddonPlan(org: OrgLike) {
  if (isActiveUserBilling(org)) return true
  return !!(planIdOf(org)?.includes('0101') || planIdOf(org)?.includes('0200'))
}

/** Plans whose add-on cannot go below its current limit (was `isEnterpriseOrPro`; false for `cu-prp-0300` by id). */
export function isEnterpriseOrProPlan(org: OrgLike) {
  if (isActiveUserBilling(org)) return true
  const planId = planIdOf(org)
  return !!(planId?.includes('organization') || planId?.includes('pro') || planId?.includes('cu-ent')
    || planId?.includes('cu-pro') || planId?.includes('cu-bus') || planId?.includes('cu-gro'))
}

/** "Manage add-on" opens contact sales instead of the add-on modal. */
export function addonOpensContactSales(org: OrgLike, plan: PlanLike) {
  return isActiveUserBilling(org) ? isActiveUserSalesOnly(plan) : shouldShowContactSalesButton(planIdOf(org))
}

/** "Manage add-on" is enabled (the billing-manager check stays at the call site). */
export function addonButtonAllowed(org: OrgLike, subscriptionPlanId: string, hasAiFeature: boolean) {
  if (isActiveUserBilling(org)) return true
  return hasAiFeature && org.product === 'CR'
    && (ALLOWED_PLANS.includes(subscriptionPlanId) || REFFERAL_ALLOWED_PLANS.includes(subscriptionPlanId))
}

/** Cancelling goes through sales. */
export function cancelThroughSales(org: OrgLike, plan: PlanLike) {
  if (isActiveUserBilling(org)) return plan?.cancelOnlyThroughSales !== false
  return plan?.level === 2 || plan?.level === 3
}

/** Reactivate, "Cancel changes" and plan changes always go through sales on active-user plans. */
export function planChangesThroughSales(org: OrgLike) {
  return isActiveUserBilling(org)
}

/** Reactivate is offered (on old plans only for the self-serve list, as today; on active-user as contact sales). */
export function showReactivate(org: OrgLike, subscriptionPlanId: string) {
  return isActiveUserBilling(org) || ALLOWED_PLANS.includes(subscriptionPlanId)
}

/** "Plan and Payment details" open without `upgrade=true` / `addon=true`. */
export function billingDetailsOpenByDefault(org: OrgLike, search: string) {
  const params = new URLSearchParams(search)
  return isActiveUserBilling(org) || params.get('upgrade') === 'true' || params.get('addon') === 'true'
}

/** Where `/upgrade` sends a workspace. */
export function upgradeRoutePath(org: OrgLike & {id: string}) {
  if (isActiveUserBilling(org)) return `/organization/${org.id}?scrollTo=settings-billing-details#settings`
  const billingService = org.billingService
  if (!billingService || billingService.pricingType === 'block' || billingService.planId?.includes('v5')
    || billingService.planId?.startsWith('cu')) {
    return `/organization/${org.id}`
  }
  return `/organization/${org.id}?scrollTo=settings-billing-details&upgrade=true#settings`
}

/** The "Billing details" button in the settings navigation. */
export function showBillingDetailsNav(org: OrgLike) {
  return !!org.billingService && (org.billingService.pricingType === 'creator' || isActiveUserBilling(org))
}

export type UserLevelOption = {users: number, price: number}

/**
 * The user levels a billing manager may buy (GNB-3307 D14): the plan's stairstep tiers above the current level, up to
 * the plan's maximum. A tier's price is the flat price of that level; the open last tier offers the plan's maximum.
 */
export function higherUserLevels(subscription: Pick<OrgBillingSubscription, 'plan'>, allowance: number): UserLevelOption[] {
  const plan = subscription.plan
  if (!plan) return []
  const max = plan.maxLicenses || Infinity
  return (plan.extraLicensesPricePerStep || [])
    .map(t => ({users: Math.min(t.endingUnit ?? max, max), price: t.price}))
    .filter(l => l.users > allowance && Number.isFinite(l.users))
    .filter((l, i, all) => all.findIndex(o => o.users === l.users) === i)
}
